import type {
  OperatorConfig,
  ReportFilterOperatorTypes,
} from '@/types/filters';
import type { SourceDataType } from '@/types/sources';

export const AGENT_SUPPORTED_OPERATORS = new Set<ReportFilterOperatorTypes>([
  'in',
  'not in',
  '>',
  '<',
  '>=',
  '<=',
]);

/** Operators whose API/UI multi-value operand is an array (`in` / `not in`). */
export const ARRAY_OPERATORS = ['in', 'not in'];

/** Tag-style operators that also send an array payload. */
export const USER_INPUT_OPERATORS = ['contains', 'does not contain'];

/**
 * Operators whose API operand must be a non-empty sequence (Malli
 * `[:sequential {:min 1} Primitive]`), even when the UI uses a single-value input.
 */
export const ARRAY_PAYLOAD_OPERATORS = [
  ...ARRAY_OPERATORS,
  ...USER_INPUT_OPERATORS,
];

export function requiresSequentialOperand(operator: string): boolean {
  return ARRAY_PAYLOAD_OPERATORS.includes(operator);
}

// Mapping of data_type to labels, operators, and input modes
export const OPERATORS_BY_DATA_TYPE: Record<
  NonNullable<SourceDataType>,
  OperatorConfig[]
> = {
  string: [
    { label: 'is', operator: 'in', inputMode: 'multiple' },
    { label: 'is not', operator: 'not in', inputMode: 'multiple' },
    { label: 'contains', operator: 'contains', inputMode: 'tags' },
    {
      label: 'does not contain',
      operator: 'does not contain',
      inputMode: 'tags',
    },
    { label: 'is empty', operator: 'is empty', inputMode: 'none' },
    { label: 'is not empty', operator: 'is not empty', inputMode: 'none' },
  ],
  number: [
    { label: 'is', operator: 'in', inputMode: 'single' },
    { label: 'is not', operator: 'not in', inputMode: 'single' },
    { label: 'greater than', operator: '>', inputMode: 'single' },
    { label: 'less than', operator: '<', inputMode: 'single' },
    { label: 'greater or equal', operator: '>=', inputMode: 'single' },
    { label: 'less or equal', operator: '<=', inputMode: 'single' },
    { label: 'is empty', operator: 'is empty', inputMode: 'none' },
    { label: 'is not empty', operator: 'is not empty', inputMode: 'none' },
  ],
  datetime: [
    { label: 'is on', operator: 'in', inputMode: 'single' },
    { label: 'is not on', operator: 'not in', inputMode: 'single' },
    { label: 'is after', operator: '>', inputMode: 'single' },
    { label: 'is before', operator: '<', inputMode: 'single' },
    { label: 'is on or after', operator: '>=', inputMode: 'single' },
    { label: 'is on or before', operator: '<=', inputMode: 'single' },
    { label: 'is empty', operator: 'is empty', inputMode: 'none' },
    { label: 'is not empty', operator: 'is not empty', inputMode: 'none' },
  ],
  boolean: [
    { label: 'is', operator: 'in', inputMode: 'single' },
    { label: 'is not', operator: 'not in', inputMode: 'single' },
    { label: 'is empty', operator: 'is empty', inputMode: 'none' },
    { label: 'is not empty', operator: 'is not empty', inputMode: 'none' },
  ],
  segment_filter: [{ label: 'is', operator: 'in', inputMode: 'single' }],
  our_segment_filter: [
    { label: 'is any of', operator: 'OR', inputMode: 'single' },
    { label: 'is all of', operator: 'AND', inputMode: 'single' },
  ],
  partner_segment_filter: [
    { label: 'is any of', operator: 'OR', inputMode: 'single' },
    { label: 'is all of', operator: 'AND', inputMode: 'single' },
  ],
};

/**
 * Get operator configs for a data type, with fallback to string operators.
 */
export function getOperatorsForDataType(
  dataType: SourceDataType,
): OperatorConfig[] {
  if (!dataType) {
    return OPERATORS_BY_DATA_TYPE.string;
  }
  return OPERATORS_BY_DATA_TYPE[dataType];
}

/**
 * Get a specific operator config by data type and operator value.
 */
export function getOperatorConfig(
  dataType: SourceDataType,
  operator: string,
): OperatorConfig | undefined {
  const operators = getOperatorsForDataType(dataType);
  return operators.find((op) => op.operator === operator);
}

/**
 * Get a human-readable label for an operator without knowing its data type.
 * Searches all data types and returns the first match.
 */
export function getOperatorLabelAnyType(operator: string): string {
  for (const configs of Object.values(OPERATORS_BY_DATA_TYPE)) {
    const config = configs.find((c) => c.operator === operator);
    if (config) return config.label;
  }
  return operator;
}

/**
 * Get the label for an operator given the data type context.
 */
export function getOperatorLabel(
  dataType: SourceDataType,
  operator: string,
): string {
  const config = getOperatorConfig(dataType, operator);
  return config?.label ?? operator;
}

type FilterDefaults = {
  operator: 'in' | '>=' | 'OR';
  operand:
    | boolean[]
    | null
    | never[]
    | Record<string, never>
    | Record<string, never[]>
    | Record<string, string | never[]>;
};

/**
 * Get default operator and operand for a data type.
 */
export function getDefaultsForDataType(
  dataType?: SourceDataType,
): FilterDefaults {
  switch (dataType) {
    case 'boolean':
      return { operator: 'in', operand: [true] };
    case 'number':
    case 'datetime':
      return { operator: '>=', operand: null };
    case 'segment_filter':
      return { operator: 'in', operand: {} };
    case 'our_segment_filter':
      return { operator: 'OR', operand: { operator: 'OR', items: [] } };
    case 'partner_segment_filter':
      return { operator: 'OR', operand: { operator: 'OR', items: [] } };
    case 'string':
    default:
      return { operator: 'in', operand: [] };
  }
}
